import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import {
  AuthService,
  CacheService,
  IQueryResult,
  Key,
  SKIP_ERROR_HEADER_KEY,
  IEntityService,
} from '@kpd/framework-ngx';
import { apiUrl } from 'environments/environment';
import { Role } from 'app/shared/models/role';
import { Observable } from 'rxjs';
import { UserManagementQueryService } from 'app/shared/services/usermanagement.query.service';
import { RoleKey } from '../models/keys/role-key';
import { IUserDTO } from 'app/shared/services/user.service';
import { User } from 'app/shared/models/user';
import { IPermissionSetDTO } from 'app/shared/services/permissionset.service';

export interface IRoleDTO {
  isAssignedByDefault: boolean;
  description: {
    dutch?: string;
    english?: string;
    french?: string;
    german?: string;
  };
  name: string;
}

export interface IRoleWithPermissionSetsDTO extends IRoleDTO {
  permissionSets: IPermissionSetDTO[];
}

export interface IPermissionDTO {
  id: string;
  name: string;
  description: string;
}

export interface IApplicationWithPermissions {
  roles: [
    {
      permissionSets: [string];
      isAssignedByDefault: true;
      name: string;
    },
  ];
  name: string;
  description: string;
  version: string;
}

export interface IApplicationDTOWithRoles {
  name: string;
  roles: IRoleDTO[];
}

@Injectable()
export class RoleService extends UserManagementQueryService implements IEntityService {
  constructor(httpClient: HttpClient, cache: CacheService, private authService: AuthService) {
    super(httpClient, cache, '_role');
  }

  get tenant(): Key<string> {
    return this.authService.getTenant().key;
  }

  getRolesByApplication(applicationName: string): Observable<Role[]> {
    return this.httpClient
      .get<IRoleDTO[]>(`${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(applicationName)}/roles`)
      .pipe(map((rolesDTO) => rolesDTO.map((dto) => new Role(dto))));
  }

  getAppsWithRoles(): Observable<IApplicationWithPermissions[]> {
    const roles$ = this.httpClient.get<IApplicationWithPermissions[]>(
      `${apiUrl()}/tenants/${this.tenant}/manageableroles`,
    );

    if (this.cache) {
      return this.cache.get(`Roles_All`, roles$, Number.MAX_VALUE);
    }
    return roles$;
  }

  getRoleWithPermissionSets(applicationName: string, roleName: string): Observable<Role> {
    return this.httpClient
      .get<IRoleWithPermissionSetsDTO>(
        `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
          applicationName,
        )}/roles/${encodeURIComponent(roleName)}`,
      )
      .pipe(map((dto) => new Role(dto)));
  }

  get(): Observable<IQueryResult> {
    return this.httpClient.get<IApplicationDTOWithRoles[]>(`${apiUrl()}/tenants/${this.tenant}/roles`).pipe(
      map((appsWithRoles) => {
        const roles: Role[] = [];
        appsWithRoles.forEach((app) => {
          app.roles.forEach((roleDTO) => {
            const roleEntity = new Role(roleDTO, app.name);
            roles.push(roleEntity);
          });
        });

        const queryResult = { totalCount: roles.length, result: roles };
        return queryResult;
      }),
    );
  }

  getById(id: any, clearCache: boolean = false): Observable<any> {
    const roleKey = id as RoleKey;
    return this.getDtoById(
      `tenants/${this.tenant}/application/${encodeURIComponent(roleKey.application)}/roles/${encodeURIComponent(
        roleKey.name,
      )}`,
      roleKey.toString(),
      clearCache,
      (dto: IRoleDTO) => new Role(dto, roleKey.application),
    );
  }

  delete(id: any): Observable<any> {
    const roleKey = id as RoleKey;
    const deleteUrl = `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
      roleKey.application,
    )}/roles/${encodeURIComponent(roleKey.name)}`;
    return this.httpClient.delete<any>(deleteUrl, {
      headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']),
    });
  }

  save(role: Role): Observable<Role> {
    if (!role.isNew()) {
      super.clearCache(role.key.toString());
      // Update
      const putUrl = `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
        (role.key as RoleKey).application,
      )}/roles/${encodeURIComponent((role.key as RoleKey).name)}`;

      return this.httpClient
        .put<any>(putUrl, role, { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']) })
        .pipe(map((dto) => new Role(dto, (role.key as RoleKey).application)));
    }

    // New
    const postUrl = `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
      role.application || '',
    )}/roles`;
    return this.httpClient
      .post<any>(postUrl, role, { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']) })
      .pipe(map((dto) => new Role(dto, role.application as string)));
  }

  removePermissionSetFromRole(key: RoleKey, permissionSet: string): Observable<any> {
    const deleteUrl = `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
      key.application,
    )}/roles/${encodeURIComponent(key.name)}/permissionSets/${encodeURIComponent(permissionSet)}`;
    return this.httpClient.delete<any>(deleteUrl).pipe(map(() => true));
  }

  addPermissionSetToRole(roleKey: RoleKey, permissionSetId: string): Observable<any> {
    return this.httpClient.post(
      `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
        roleKey.application,
      )}/roles/${encodeURIComponent(roleKey.name)}/permissionsets/${encodeURIComponent(permissionSetId)}`,
      {},
    );
  }

  getRoleUsers(roleKey: RoleKey): Observable<User[]> {
    return this.httpClient
      .get<IUserDTO[]>(
        `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
          roleKey.application,
        )}/roles/${encodeURIComponent(roleKey.name)}/users`,
      )
      .pipe(map((usersDTO) => usersDTO.map((dto) => new User(dto))));
  }

  createUserRole(roleKey: RoleKey, upn: string): Observable<any> {
    return this.httpClient.post(
      `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
        roleKey.application,
      )}/roles/${encodeURIComponent(roleKey.name)}/users/${encodeURIComponent(upn)}`,
      {},
      { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409']) },
    );
  }

  deleteUserRole(roleKey: RoleKey, upn: string): Observable<any> {
    return this.httpClient.delete(
      `${apiUrl()}/tenants/${this.tenant}/application/${encodeURIComponent(
        roleKey.application,
      )}/roles/${encodeURIComponent(roleKey.name)}/users/${encodeURIComponent(upn)}`,
      { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['404']) },
    );
  }

  clearRolesCache(): void {
    if (this.cache) {
      this.cache.remove(`Roles_All`);
    }
  }
}
